import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { selectCardsById } from '../Cards/selectors';
import { describeEntry } from '../GameLog/describeEntry';
import LogText from '../GameLog/LogText';
import CardFace from '../Card/CardFace';
import { NewMark } from '../Hand/styled';
import { PlainButton } from '../Controls';
import CardZoom from '../CardZoom';
import { Lines, Position, ReplayHand, ReplayOverlay, Steps, StepButton } from './styled';

/**
 * The replay's controls, over the turn banner and hand while replaying (so the board keeps its size): which move,
 * the move in words, back and forward, and your hand as it was then.
 */
const ReplayPanel = ({ replay, userId, onDone }) => {
  const cardsById = useSelector(selectCardsById);
  const [zoomed, setZoomed] = useState(null);
  const { entries, index, entry, step } = replay;

  if (!entries || !entry) {
    return (
      <ReplayOverlay>
        <Steps><StepButton onClick={onDone}>Done</StepButton></Steps>
      </ReplayOverlay>
    );
  }

  const lines = describeEntry(entry, { me: userId, cardsById });
  const handAt = (at) => {
    const side = at && at.board.find(s => s.user_id === userId);
    return (side && side.hand_cards) || [];
  };
  const hand = handAt(entry);
  // What this move put in your hand glows, as a card drawn does in the game; it only changes on your own moves.
  const before = [...handAt(entries[index - 1])];
  const fresh = hand.map((cardId) => {
    const at = before.indexOf(cardId);
    if (at === -1) {
      return true;
    }
    before.splice(at, 1);
    return false;
  });
  const last = entries.length - 1;

  return (
    <ReplayOverlay>
      <Position>
        {entry.turn ? `Turn ${entry.turn}` : 'Setup'} · {entry.user_id === userId ? 'You' : 'Opponent'} · move {index + 1} of {entries.length}
      </Position>
      <Lines aria-live="polite">
        {lines.length ? lines.map((line, i) => <li key={i}><LogText text={line.text} /></li>) : <li>Nothing to see in this move.</li>}
      </Lines>
      <Steps role="group" aria-label="Replay">
        <StepButton onClick={() => step(-1)} disabled={index === 0} aria-label="Back a move">◀ Back</StepButton>
        <StepButton $primary onClick={() => step(1)} disabled={index >= last} aria-label="Forward a move">Forward ▶</StepButton>
        <StepButton onClick={onDone}>Done</StepButton>
      </Steps>
      <ReplayHand data-replay-hand aria-label={`Your hand then: ${hand.length} cards`}>
        {hand.map((cardId, i) => (
          <li key={`${cardId}-${i}`} data-card-id={cardId}>
            <PlainButton onClick={() => setZoomed(cardsById.get(cardId))} aria-label={cardsById.getIn([cardId, 'name'])}>
              <NewMark $new={index > 0 && fresh[i]}><CardFace card={cardsById.get(cardId)} /></NewMark>
            </PlainButton>
          </li>
        ))}
      </ReplayHand>
      {zoomed && <CardZoom card={zoomed} onClose={() => setZoomed(null)} />}
    </ReplayOverlay>
  );
};

ReplayPanel.propTypes = {
  replay: PropTypes.object.isRequired,
  userId: PropTypes.number.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default ReplayPanel;
